import type { AnalyticsProps, GtagEvent } from '../../types/assertive'
import useOutbrainAds from '../../composables/ad-ops/useOutbrainAds'
import debug from '../../utils/console'
import { priceBuckets } from './normalizePricePointBuckets'
const logger = debug.extend('adFunctions')

export function stripAYCode(placement: string): string {
  // Strip away "__ayManagerEnv__XX..." suffix off the element ID to get the placement class
  return placement.replace(/__ayManagerEnv__.+$/, '')
}

export function setupNativeAdAsFallback(event: GtagEvent) {
  // This function is used to render a native ad in case the Adx script returns empty.
  const elementId = event.slot.getSlotElementId() // Element id for Adx script
  const placementElement = document.getElementById(elementId)?.parentElement
  const placementClass = stripAYCode(elementId)

  if (!placementElement) {
    logger.error('Element not found for native ad fallback', placementClass)
    return
  }

  if (event.isEmpty) {
    try {
      const { renderFallbackOutbrainAd } = useOutbrainAds()
      window.ayManagerEnv.cmd.push(function () {
        window.ayManagerEnv.destroy([elementId], {
          useInstanceCodes: true,
        })
      })
      renderFallbackOutbrainAd(placementClass, placementElement)
    } catch (err) {
      logger.error(
        'Error on Gtag slotRenderEnded listener for native ad fallback',
        err
      )
    }
  }
}

export function getFallbackPlacementWidget(
  placement: string
): OutbrainProps | undefined {
  const placementConfigs = useAppConfig().adops?.placements

  if (!placementConfigs) {
    logger.error('No outbrain widgets found')
    return
  }

  if (!placementConfigs || !placementConfigs[placement]?.outbrain) {
    console.error('No outbrain widget found for: ', placement)
    return
  }

  // return regular placement name so its targeted for removal in classList
  return { ...placementConfigs[placement].outbrain, fallback: true, placement }
}

// This function is used to get the Outbrain widget ID for a specific placement.
// Used to set the dynamic price for the placement.
export function getOutbrainWidgetIdForPlacement(
  placement: string
): string | undefined {
  const widgets = useAppConfig().outbrain?.mapPlacementToWidget

  if (!widgets) {
    logger.error('No mapping for placement to outbrain widgets found.')
    return
  }

  if (!widgets || !widgets[placement]) {
    logger.error('No outbrain widget found for: ', placement)
    return
  }

  return widgets[placement]
}

export function getIndexOfPlacement(
  placement: string,
  el: Element | null
): number {
  if (!el || !placement) {
    logger.error('Element is null for placement:', placement)
    return 0
  }

  const placementDivs = document.querySelectorAll(`.${placement}`)

  if (placementDivs.length > 1 && el) {
    const index = Array.from(placementDivs).findIndex((div) => div === el)
    if (index !== -1) {
      return index
    }
  }
  return 0
}

export function handleStickyAd(placementDiv: Element, timeout = 3000): void {
  if (!placementDiv) {
    logger.error('No element to handle sticky ad')
    return
  }

  if (!placementDiv.classList.contains('sticky')) {
    clearTimeout(timeout)
  }

  placementDiv.classList.add('sticky')
  placementDiv.classList.add('top-0')
  placementDiv.classList.add('z-[1000]')

  if (timeout && !isNaN(timeout)) {
    setTimeout(() => {
      placementDiv.classList.remove('sticky')
      placementDiv.classList.remove('top-0')
      placementDiv.classList.remove('z-[1000]')
    }, timeout)
  }
}

const floorConfig = {
  enabled: true,
  currency: 'USD',
  prebid: true,
  priceBuckets,
}

export async function injectAYSdk(
  ayEntityId: string | unknown,
  analyticsProps: AnalyticsProps,
  isDynamicFloorEnabled: boolean | undefined,
  layoutName: string
) {
  if (window.assertive && window.ayManagerEnv) {
    return {
      ayManagerEnv: window.ayManagerEnv,
      assertive: window.assertive,
    }
  }

  const floorOption = isDynamicFloorEnabled ? { floor: floorConfig } : {}

  try {
    if (ayEntityId) {
      await loadScriptAsync('head', {
        src: `https://${ayEntityId}.ay.delivery/manager/${ayEntityId}`,
      })
    }
  } catch (error) {
    logger.error('Failed to load Assertive Yield SDK:', error)
    return {
      ayManagerEnv: null,
      assertive: null,
      loading: false,
    }
  }

  const analyticsOptions = {
    integrations: { webAnalytics: true },
    logUnfilled: true,
    custom: {
      layout: layoutName,
      userState: null,
      custom_1: analyticsProps.type_tag,
      custom_2: analyticsProps.ntp_app_version,
      custom_3: analyticsProps.host_browser_app_version,
      custom_4: getTruncatedUrl(),
    },
    useHistoryChangeTrigger: true,
    ...floorOption,
  }
  const assertiveOptions = {
    ayEntityId,
    floor: isDynamicFloorEnabled ? floorConfig : {},
    debug: true,
    analytics: analyticsOptions,
  }

  window.assertiveYieldAnalytics ??= { ...analyticsOptions }
  window.assertive ??= { ...assertiveOptions }
  window.ayManagerEnv ??= { cmd: [] }

  return {
    ayManagerEnv: window.ayManagerEnv,
    assertive: window.assertive,
    loading: true,
  }
}
